import React, { createContext, useContext, useEffect, useMemo, useReducer } from 'react';
import { createSeedState, STATE_VERSION } from '../data/seed';
import type { RegistryState } from '../data/types';
import { holdingsForState, type Holding } from '../domain/holdings';
import { DEMO_TODAY } from '../lib/format';
import type { RegistryAction } from './actions';
import { registryReducer } from './reducer';

const STORAGE_KEY = 'registryPrototype.state';

interface RegistryContextValue {
  state: RegistryState;
  dispatch: React.Dispatch<RegistryAction>;
  holdings: Holding[];
}

const RegistryContext = createContext<RegistryContextValue | null>(null);

function loadInitialState(): RegistryState {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as RegistryState;
      if (parsed.version === STATE_VERSION) return parsed;
    }
  } catch {
    // Storage unavailable or corrupt: fall back to fresh seed data.
  }
  return createSeedState();
}

export function RegistryProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(registryReducer, undefined, loadInitialState);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } catch {
      // Quota or privacy mode: the demo still works for this session.
    }
  }, [state]);

  const holdings = useMemo(() => holdingsForState(state, DEMO_TODAY), [state]);
  const value = useMemo(() => ({ state, dispatch, holdings }), [state, holdings]);
  return <RegistryContext.Provider value={value}>{children}</RegistryContext.Provider>;
}

export function useRegistry(): RegistryContextValue {
  const value = useContext(RegistryContext);
  if (!value) throw new Error('useRegistry must be used inside RegistryProvider');
  return value;
}
